import React, { createContext, useContext, ReactNode, useState } from 'react';
import ApiService from '../../infrastructure/api/ApiService';
import { ApiResponse } from '../../domain/models/api';

// API Context için tipini tanımla
interface ApiContextType {
  apiService: typeof ApiService;
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;
  executeRequest: <T>(
    requestFn: () => Promise<ApiResponse<T>>,
    successCallback?: (data: T | null) => void,
    errorCallback?: (error: string) => void
  ) => Promise<ApiResponse<T>>;
}

// Context oluştur
const ApiContext = createContext<ApiContextType | undefined>(undefined);

// Context Provider bileşeni
export const ApiContextProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hata temizleme
  const clearError = () => setError(null);

  // ApiService instance'ı
  const apiService = ApiService;

  // İstek yürütme yardımcı fonksiyonu
  const executeRequest = async <T,>(
    requestFn: () => Promise<ApiResponse<T>>,
    successCallback?: (data: T | null) => void,
    errorCallback?: (error: string) => void
  ): Promise<ApiResponse<T>> => {
    clearError();
    setLoading(true);

    try {
      const response = await requestFn();
      
      if (!response.success) {
        setError(response.error || 'İşlem başarısız oldu');
        errorCallback?.(response.error || 'İşlem başarısız oldu');
      } else {
        successCallback?.(response.data || null);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Beklenmeyen bir hata oluştu';
      setError(errorMessage);
      errorCallback?.(errorMessage);
      
      return {
        success: false,
        error: errorMessage,
        data: null
      };
    } finally {
      setLoading(false);
    }
  };

  // Context değerini oluştur
  const contextValue: ApiContextType = {
    apiService,
    isLoading,
    setLoading,
    error,
    setError,
    clearError,
    executeRequest
  };

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
};

// Context kullanımı için hook
export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  
  if (context === undefined) {
    throw new Error('useApi hook must be used within an ApiContextProvider');
  }
  
  return context;
};

export default ApiContext; 